/**
 * API Configuration
 * Centralized configuration for all API calls
 */

// Get API URL from environment variable or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || '';
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

/**
 * Base API configuration
 */
export const apiConfig = {
  baseURL: API_BASE_URL,
  endpoints: {
    // Auth endpoints
    auth: {
      login: '/api/auth/login',
      microsoftLogin: '/api/auth/microsoft',
      microsoftStatus: '/api/auth/microsoft/status',
      logout: '/api/auth/logout',
      refresh: '/api/auth/refresh',
      me: '/api/auth/me'
    },
    // Health check
    health: '/health',
    // Contract endpoints
    contracts: {
      base: '/api/contracts',
      getAll: '/api/contracts',
      getOne: (id: string) => `/api/contracts/${id}`,
      create: '/api/contracts',
      update: (id: string) => `/api/contracts/${id}`,
      delete: (id: string) => `/api/contracts/${id}`,
      // File operations
      repository: '/api/contracts/repository',
      repositoryExtract: '/api/contracts/repository/extract',
      repositoryCoverage: '/api/contracts/repository/coverage',
      uploadFile: (id: string) => `/api/contracts/${id}/files/upload`,
      getFiles: (id: string) => `/api/contracts/${id}/files`,
      deleteFile: (id: string, fileId: string) => `/api/contracts/${id}/files/${fileId}`,
      downloadFile: (fileId: string) => `/api/contracts/files/${fileId}/download`,
      // Notes operations
      addNote: (id: string) => `/api/contracts/${id}/notes`,
      getNotes: (id: string) => `/api/contracts/${id}/notes`,
      editNote: (id: string, noteId: string) => `/api/contracts/${id}/notes/${noteId}`,
      deleteNote: (id: string, noteId: string) => `/api/contracts/${id}/notes/${noteId}`,
      // Analysis
      analyze: (id: string) => `/api/contracts/${id}/analyze`,
      getAnalysis: (id: string) => `/api/contracts/${id}/analysis`,
      getAnalysisVersions: (id: string) => `/api/contracts/${id}/analysis/versions`,
      getAnalysisVersion: (id: string, version: number) => `/api/contracts/${id}/analysis/version/${version}`,
      // Clause decisions (linked to analysis)
      getClauseDecisions: (id: string, analysisId: string) => `/api/contracts/${id}/analysis/${analysisId}/clauses/decisions`,
      updateClauseDecision: (id: string, analysisId: string, clauseRef: string) => `/api/contracts/${id}/analysis/${analysisId}/clauses/${encodeURIComponent(clauseRef)}/decision`,
      addClauseComment: (id: string, analysisId: string, clauseRef: string) => `/api/contracts/${id}/analysis/${analysisId}/clauses/${encodeURIComponent(clauseRef)}/comments`,
      // Workflow state
      updateWorkflowState: (id: string) => `/api/contracts/${id}/workflow`,
      // Negotiation
      generateNegotiationEmail: '/api/contracts/generate-negotiation-email',
      // Generation checkpoint (for auto-save during long email generation)
      getGenerationCheckpoint: (id: string) => `/api/contracts/${id}/generation-checkpoint`,
      resumeEmailGeneration: (id: string) => `/api/contracts/${id}/resume-email-generation`,
      clearGenerationCheckpoint: (id: string) => `/api/contracts/${id}/generation-checkpoint`
    },
    // Supplier endpoints
    suppliers: {
      base: '/api/suppliers',
      getAll: '/api/suppliers',
      getOne: (id: string) => `/api/suppliers/${id}`,
      create: '/api/suppliers',
      update: (id: string) => `/api/suppliers/${id}`,
      delete: (id: string) => `/api/suppliers/${id}`,
      search: '/api/suppliers/search',
      getContracts: (id: string) => `/api/suppliers/${id}/contracts`
    },
    rules: {
      getAll: '/api/rules',
      create: '/api/rules',
      update: (id: string) => `/api/rules/${id}`,
      delete: (id: string) => `/api/rules/${id}`,
      toggle: (id: string) => `/api/rules/${id}/toggle`,
      history: (id: string) => `/api/rules/${id}/history`,
      suggestions: '/api/rules/suggestions',
      importTemplate: '/api/rules/import/template',
      import: '/api/rules/import',
      suggestionAction: (id: string, action: 'accept' | 'dismiss' | 'restore') =>
        `/api/rules/suggestions/${id}/${action}`,
      playbook: (playbookId: string) => `/api/rules/playbook/${encodeURIComponent(playbookId)}`,
      playbookParameter: (playbookId: string, parameterId: string) =>
        `/api/rules/playbook/${encodeURIComponent(playbookId)}/${encodeURIComponent(parameterId)}`
    },
    // Session/Autosave endpoints
    session: {
      save: '/api/session',
      load: '/api/session'
    },
    // AI endpoints
    ai: {
      status: '/api/ai/status',
      analyze: '/api/ai/analyze',
      extractText: '/api/ai/extract-text',
      generateEmail: '/api/ai/generate-email'
    },
    // Contract Groups endpoints
    contractGroups: {
      base: '/api/contract-groups',
      getAll: '/api/contract-groups',
      getActive: '/api/contract-groups/active',
      getOne: (id: string) => `/api/contract-groups/${id}`,
      create: '/api/contract-groups',
      update: (id: string) => `/api/contract-groups/${id}`,
      delete: (id: string) => `/api/contract-groups/${id}`
    },
    // Document Generator endpoints
    documentGenerator: {
      // Templates
      templates: {
        base: '/api/document-generator/templates',
        getAll: '/api/document-generator/templates',
        getOne: (id: string) => `/api/document-generator/templates/${id}`,
        create: '/api/document-generator/templates',
        update: (id: string) => `/api/document-generator/templates/${id}`,
        delete: (id: string) => `/api/document-generator/templates/${id}`,
        archive: (id: string) => `/api/document-generator/templates/${id}/archive`,
        restore: (id: string) => `/api/document-generator/templates/${id}/restore`,
        clone: (id: string) => `/api/document-generator/templates/${id}/clone`,
        lockClause: (id: string) => `/api/document-generator/templates/${id}/lock-clause`,
        unlockClause: (id: string, clauseId: string) => `/api/document-generator/templates/${id}/unlock-clause/${clauseId}`,
        addJurisdiction: (id: string) => `/api/document-generator/templates/${id}/jurisdictions`,
        updateJurisdiction: (id: string, code: string) => `/api/document-generator/templates/${id}/jurisdictions/${code}`,
        removeJurisdiction: (id: string, code: string) => `/api/document-generator/templates/${id}/jurisdictions/${code}`
      },
      // Generated Documents
      documents: {
        base: '/api/document-generator/documents',
        getAll: '/api/document-generator/documents',
        getOne: (id: string) => `/api/document-generator/documents/${id}`,
        generate: '/api/document-generator/documents/generate',
        update: (id: string) => `/api/document-generator/documents/${id}`,
        delete: (id: string) => `/api/document-generator/documents/${id}`,
        preview: (templateId: string) => `/api/document-generator/templates/${templateId}/preview`,
        downloadPdf: (id: string) => `/api/document-generator/documents/${id}/pdf`,
        downloadDocx: (id: string) => `/api/document-generator/documents/${id}/docx`,
        addAttachment: (id: string) => `/api/document-generator/documents/${id}/attachments`,
        deleteAttachment: (id: string, attachmentId: string) => `/api/document-generator/documents/${id}/attachments/${attachmentId}`,
        compareVersions: (id: string) => `/api/document-generator/documents/${id}/compare`,
        getVersion: (id: string, version: number) => `/api/document-generator/documents/${id}/versions/${version}`,
        // Approval endpoints
        submitForApproval: (id: string) => `/api/document-generator/documents/${id}/submit-for-approval`,
        approvalAction: (id: string) => `/api/document-generator/documents/${id}/approval-action`,
        approvalHistory: (id: string) => `/api/document-generator/documents/${id}/approval-history`,
        recall: (id: string) => `/api/document-generator/documents/${id}/recall`,
        sign: (id: string) => `/api/document-generator/documents/${id}/sign`
      },
      // Approval Workflows
      workflows: {
        base: '/api/document-generator/workflows',
        getAll: '/api/document-generator/workflows',
        getOne: (id: string) => `/api/document-generator/workflows/${id}`,
        create: '/api/document-generator/workflows',
        update: (id: string) => `/api/document-generator/workflows/${id}`,
        delete: (id: string) => `/api/document-generator/workflows/${id}`
      },
      // Pending Approvals
      pendingApprovals: '/api/document-generator/pending-approvals',
      // Jurisdictions
      jurisdictions: '/api/document-generator/jurisdictions',
      // AI Features
      ai: {
        status: '/api/document-generator/ai/status',
        improve: '/api/document-generator/ai/improve',
        alternatives: '/api/document-generator/ai/alternatives',
        compliance: '/api/document-generator/ai/compliance',
        generateClause: '/api/document-generator/ai/generate-clause',
        simplify: '/api/document-generator/ai/simplify',
        summarizeChanges: '/api/document-generator/ai/summarize-changes',
        analyzePlaceholders: '/api/document-generator/ai/analyze-placeholders'
      }
    }
  }
};

/**
 * Default headers for API requests
 */
export const getDefaultHeaders = (token: string | null = null): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * API Response interface
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Handle API response
 * @param {Response} response - Fetch API response
 * @returns {Promise<ApiResponse>} Parsed response data
 */
export const handleApiResponse = async <T = any>(response: Response): Promise<ApiResponse<T>> => {
  // Check if response is ok
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: `HTTP error! status: ${response.status}`
    }));
    throw new Error(error.message || `Request failed with status ${response.status}`);
  }
  
  // Parse JSON response
  const data = await response.json();
  
  // Check if the response indicates success
  if (data.success === false) {
    throw new Error(data.message || 'Request failed');
  }
  
  return data;
};

/**
 * Make API request with authentication
 * @param {string} url - API endpoint URL
 * @param {RequestInit} options - Fetch options
 * @param {string} token - Authentication token
 * @returns {Promise<ApiResponse>} Response data
 */
export const makeApiRequest = async <T = any>(
  url: string, 
  options: RequestInit = {}, 
  token: string | null = null
): Promise<ApiResponse<T>> => {
  try {
    const headers = {
      ...getDefaultHeaders(token),
      ...(options.headers as Record<string, string> || {})
    };
    
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers
    });
    
    return await handleApiResponse<T>(response);
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

export default apiConfig;
